import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { validateAge } from '@wineryxplorer/shared';
import { useAuth } from '../../hooks/useAuth';
import { useAgeAffirmation } from '../../lib/age/useAgeAffirmation';
import { birthdayToDate } from '../auth/loginValidation';
import { Typography } from '../ui/Typography';
import { AgeGateScreen } from './AgeGateScreen';

interface AgeGateProps {
  children: React.ReactNode;
}

/**
 * Routes that render no alcohol content and must stay reachable to someone
 * who has not affirmed yet.
 *
 * The callbacks are plumbing: an OAuth or email-verification round trip lands
 * here from an external redirect, and swallowing it behind the gate would fail
 * the sign-in silently with no way to retry. The legal pages are the ones a
 * refused visitor is most entitled to read. Nothing here lists a winery, a
 * wine or an event — exempting a content route would defeat the gate.
 */
const UNGATED_PATHS = [
  '/oauth/callback',
  '/verify-email',
  '/verify-email/callback',
  '/privacy',
  '/terms',
];

/**
 * Blocks all content until legal drinking age is affirmed.
 *
 * Alcohol content has to be gated before it is displayed, not before an
 * account is created — most visitors browse without ever registering, so a
 * signup-only gate would serve wineries and wines to unverified visitors
 * indefinitely.
 *
 * Renders in place of its children rather than redirecting to a route: a route
 * can be linked past, and a redirect would race the guards below this one.
 *
 * A signed-in user who finished onboarding already supplied a birthday that
 * was validated server-side, so they are treated as already affirmed. That is
 * derived directly from `user.onboardingComplete` rather than routed through
 * `affirm()` first, so the gate never flashes at them while the flag is
 * written. The effect below still calls `affirm()` for such a user, but only
 * to persist the outcome for next time — it is not what decides whether
 * children render here.
 */
export const AgeGate = ({ children }: AgeGateProps) => {
  const { status, affirm } = useAgeAffirmation();
  const { user, isLoading, isApiUnreachable } = useAuth();
  const { pathname } = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);

  const onboarded = Boolean(user?.onboardingComplete);
  const ungated = UNGATED_PATHS.includes(pathname);
  const affirmed = status === 'affirmed' || onboarded;

  useEffect(() => {
    if (status === 'unaffirmed' && onboarded) {
      affirm();
    }
  }, [status, onboarded, affirm]);

  const handleAffirm = useCallback(
    (value: string, country: string) => {
      // An unusable date and a failed age check are different outcomes: a typo
      // deserves another go, being under age does not. So the date has to be
      // established as real and in the past before it is judged — otherwise a
      // mistyped future year reads as "negative age" and hard-stops someone
      // who only fat-fingered the decade.
      const birthday = birthdayToDate(value);

      if (Number.isNaN(birthday.getTime())) {
        setError('Please enter a complete date of birth.');
        return;
      }

      if (birthday.getTime() > Date.now()) {
        setError('Your date of birth cannot be in the future.');
        return;
      }

      if (validateAge(birthday, country)) {
        // Refusal is a stop. Nothing is stored, and the date is not handed
        // back in an editable field for an immediate retry.
        setRefused(true);
        return;
      }

      setError(null);
      affirm();
    },
    [affirm]
  );

  if (ungated) return <>{children}</>;

  if (refused) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center px-8 text-center"
        data-testid="age-gate-refused"
      >
        <div className="max-w-md">
          <Typography variant="heading2" as="h1">
            We cannot let you in
          </Typography>
          <Typography variant="bodyMedium" className="mt-2 text-gray-600">
            You need to be of legal drinking age in your country to use
            WineryXplorer. Thank you for stopping by.
          </Typography>
        </div>
      </div>
    );
  }

  if (affirmed) return <>{children}</>;

  // While the first `getMe` is still in flight we do not yet know whether this
  // is an onboarded user who should never see the gate at all. Waiting it out
  // avoids flashing the gate at them on every cold load. Once the API is
  // declared unreachable the answer is never coming, so fall through to the
  // gate rather than leaving a permanently blank page. This check sits below
  // the `affirmed` short-circuit on purpose: an already-affirmed browser
  // returns children immediately and is never blanked while its session
  // resolves.
  if (isLoading && !isApiUnreachable) return null;

  return <AgeGateScreen onAffirm={handleAffirm} error={error} />;
};
